/**
 * Display formatting for the home desk (PPL-407).
 *
 * The payload carries ISO instants in UTC and bare counts; these helpers turn
 * them into the lowercase house voice in the viewer's own time zone. They
 * never compose a NAME — names arrive from the server already in the right
 * identity register — they only join, pluralise and stamp what they are given.
 */

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const SMALL = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/**
 * Parse an ISO instant OR a bare `YYYY-MM-DD` day. A bare day is a calendar
 * date, not an instant, so it is built from its parts in LOCAL time — parsing
 * it as UTC would shift a birthday a day early west of Greenwich.
 */
export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Whole calendar days from `now` to `date`, in local time (+1 = tomorrow). */
export function calendarDaysUntil(date: Date, now: Date = new Date()): number {
  return Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000);
}

/** "3 pm" · "3:15 pm" · "15:00" — the viewer's locale, lowercase, no ":00" in 12-hour clocks. */
export function formatClock(value: string, now?: Date): string {
  void now;
  const d = parseDate(value);
  if (!d) return '';
  const raw = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d).toLowerCase();
  return /[ap]m/.test(raw) ? raw.replace(/:00(?=\s*[ap]m)/, '').replace(/\s+/, ' ') : raw;
}

/** "4 sep" — day and short month, lowercase. */
export function formatShortDate(value: string): string {
  const d = parseDate(value);
  if (!d) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/**
 * The day a thing falls on, the way f8 would say it: today · tomorrow ·
 * yesterday · a weekday when it is within the week either way · "4 sep" beyond.
 */
export function dayLabel(value: string, now: Date = new Date()): string {
  const d = parseDate(value);
  if (!d) return '';
  const diff = calendarDaysUntil(d, now);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  if (Math.abs(diff) < 7) return WEEKDAYS[d.getDay()];
  return formatShortDate(value);
}

/**
 * M7's `when` is documented as a string; the contract says instants are ISO,
 * but a server that already rendered "yesterday" should not be re-rendered
 * into nonsense. A parseable value becomes a day label, anything else passes
 * through untouched.
 */
export function whenLabel(value: string, now: Date = new Date()): string {
  return parseDate(value) ? dayLabel(value, now) : value;
}

/** "3 days" · "a week" · "3 weeks" · "a month" · "4 months" · "a year" · "2 years" */
export function sinceLabel(days: number): string {
  const n = Math.max(0, Math.round(days));
  if (n < 7) return `${n} ${n === 1 ? 'day' : 'days'}`;
  if (n < 14) return 'a week';
  if (n < 28) return `${Math.floor(n / 7)} weeks`;
  if (n < 60) return 'a month';
  if (n < 365) return `${Math.floor(n / 30)} months`;
  const years = Math.floor(n / 365);
  return years === 1 ? 'a year' : `${years} years`;
}

/** "A" · "A and B" · "A, B and C" · "A, B and 3 others" — names come in as given. */
export function joinNames(names: string[], othersAfter = 3): string {
  const list = names.filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length > othersAfter) {
    const shown = list.slice(0, othersAfter - 1);
    const rest = list.length - shown.length;
    return `${shown.join(', ')} and ${rest} ${rest === 1 ? 'other' : 'others'}`;
  }
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** `plural(3, 'thing')` → "3 things"; `plural(1, 'reply', 'replies')` → "1 reply". */
export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Counts under ten read as words in f8's voice ("three things to review"). */
export function smallNumber(n: number): string {
  return n >= 0 && n < SMALL.length ? SMALL[n] : String(n);
}

/** Possessive that copes with names ending in s the way people write them. */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/** The anonymous register renders the @handle AS the name — exactly one @. */
export function atHandle(handle: string): string {
  return `@${handle.replace(/^@+/, '')}`;
}

/** The one-character mark an avatar falls back to, lowercase like the canvas. */
export function handleInitial(handle: string): string {
  const bare = handle.replace(/^@+/, '');
  return bare ? bare[0].toLowerCase() : '';
}

/** Sentence case for a body line assembled from lowercase parts. */
export function sentence(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

/**
 * The undo row's line after a dismiss. `until` is homeDismissEurW's answer:
 * an instant the module stays hidden until, or null for "not again".
 */
export function undoLabel(until: string | null, now: Date = new Date()): string {
  const d = parseDate(until);
  if (!d) return 'okay — not again';
  const days = calendarDaysUntil(d, now);
  if (days <= 1) return 'okay — not until tomorrow';
  return `okay — not for ${days} days`;
}
